'use client';

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, LoadingButton, Typography } from '@eventbrite/marmalade';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { Modal } from '@/src/listings/components/_shared/Modal/Modal';
import { GACodes } from '@/src/listings/components/_shared/constants';
import { submitRsvpCampaignResponse } from './rsvpApiClient';
import styles from './RSVPModal.module.scss';
import { RSVPResponseType } from './types';

export interface RSVPModalProps {
  visible: boolean;
  onClose: () => void;
  responseType: RSVPResponseType;
  eventId: string;
  mipaToken: string | null;
}

export const RSVPModal: React.FC<RSVPModalProps> = ({ visible, onClose, responseType, eventId, mipaToken }) => {
  const { formatMessage } = useIntl();
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (!mipaToken) {
        throw new Error('Campaign token is required for this response');
      }
      await submitRsvpCampaignResponse({
        eventId,
        campaignToken: mipaToken,
        response: responseType,
        message: note.trim() || undefined,
      });
      if (responseType === 'maybe') {
        trackAnalyticsEvent({
          category: GACodes.GA_CATEGORY_LISTING,
          action: GACodes.GA_ACTION_RSVP_MAYBE,
          label: GACodes.GA_LABEL_RSVP_MAYBE_MSG_SENT,
        });
      } else {
        trackAnalyticsEvent({
          category: GACodes.GA_CATEGORY_LISTING,
          action: GACodes.GA_ACTION_RSVP_NO,
          label: GACodes.GA_LABEL_RSVP_NO_MSG_SENT,
        });
      }
      onClose();
    } catch (e) {
      console.error('Error submitting RSVP response', e);
      setError('Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const attendanceOptionTitle =
    responseType === 'maybe'
      ? formatMessage({
          id: 'Maybe attending',
          defaultMessage: 'Maybe attending',
          description: 'RSVP Maybe attending modal title',
        })
      : formatMessage({
          id: 'Not attending',
          defaultMessage: 'Not attending',
          description: 'RSVP Not attending modal title',
        });

  return (
    <Modal visible={visible} onClose={onClose} type="simple">
      <div className={styles.content}>
        <Typography
          variant="body-lg-bold"
          as="h2"
          align="center"
          className={styles.attendanceOptionTitle}
          color="neutral-800"
        >
          {attendanceOptionTitle}
        </Typography>
        <Typography variant="body-md" as="h3" align="center">
          <FormattedMessage
            id="Let the organizer know more about your plans"
            defaultMessage="Let the organizer know more about your plans"
            description="RSVP modal note label"
          />
        </Typography>
        <textarea
          id="rsvp-note"
          className={styles.noteField}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={formatMessage({
            id: 'Send an optional note to the event organizer',
            defaultMessage: 'Send an optional note to the event organizer',
          })}
          aria-label="Optional note"
          disabled={isSubmitting}
        />
        {error && <Typography variant="body-md-bold">{error}</Typography>}
        <div className={styles.actions}>
          <LoadingButton variant="primary" onClick={handleSend} isLoading={isSubmitting}>
            <FormattedMessage id="Send" defaultMessage="Send" description="RSVP modal send" />
          </LoadingButton>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting} className={styles.cancelButton}>
            <FormattedMessage id="Cancel" defaultMessage="Cancel" description="RSVP modal cancel" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};
